import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
} from 'recharts';

export type PlasticityAxisKey =
  | 'guardedness'
  | 'warmth'
  | 'directness'
  | 'trust'
  | 'curiosity';

const AXES: Array<{
  key: PlasticityAxisKey;
  label: string;
  low: string;
  high: string;
}> = [
  { key: 'guardedness', label: 'Guardedness', low: 'Open', high: 'Guarded' },
  { key: 'warmth', label: 'Warmth', low: 'Cool', high: 'Warm' },
  { key: 'directness', label: 'Directness', low: 'Tentative', high: 'Direct' },
  { key: 'trust', label: 'Trust', low: 'Skeptical', high: 'Trusting' },
  { key: 'curiosity', label: 'Curiosity', low: 'Reserved', high: 'Inquisitive' },
];

const AXIS_COLORS: Record<PlasticityAxisKey, string> = {
  guardedness: 'bg-slate-400',
  warmth: 'bg-rose-400',
  directness: 'bg-amber-400',
  trust: 'bg-emerald-400',
  curiosity: 'bg-sky-400',
};

type PlasticityRadarProps = {
  axes: Partial<Record<PlasticityAxisKey, number>>;
  trends?: Partial<Record<PlasticityAxisKey, number>>;
};

function formatTrend(trend?: number) {
  if (trend === undefined) return '—';
  if (Math.abs(trend) < 0.01) return '→ 0%';
  const arrow = trend > 0 ? '▲' : '▼';
  return `${arrow} ${(Math.abs(trend) * 100).toFixed(0)}%`;
}

export function PlasticityRadar({ axes, trends }: PlasticityRadarProps) {
  const chartData = AXES.map((axis) => ({
    dimension: axis.label,
    value: (axes[axis.key] ?? 0.5) * 100,
    fullMark: 100,
  }));

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row items-center gap-6">
        <div className="w-[280px] h-[220px] flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
              <PolarGrid stroke="#4a4a4a" />
              <PolarAngleAxis dataKey="dimension" tick={{ fill: '#a0a0a0', fontSize: 11 }} />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 100]}
                tick={{ fill: '#707070', fontSize: 9 }}
                tickCount={5}
              />
              <Radar
                name="Plasticity"
                dataKey="value"
                stroke="#9cb3c9"
                fill="#9cb3c9"
                fillOpacity={0.35}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="flex-1 space-y-3 w-full">
          {AXES.map((axis) => {
            const value = axes[axis.key] ?? 0.5;
            const trend = trends?.[axis.key];
            return (
              <div key={axis.key} className="flex items-center gap-2">
                <span className="w-20 text-xs text-parchment">{axis.label}</span>
                <span className="w-20 text-[10px] text-mist/50 text-right">{axis.low}</span>
                <div className="flex-1 h-2 bg-stone/30 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${AXIS_COLORS[axis.key]}`}
                    style={{ width: `${value * 100}%` }}
                  />
                </div>
                <span className="w-24 text-[10px] text-mist/50">{axis.high}</span>
                <span className="w-12 text-xs text-mist/80 text-right">
                  {(value * 100).toFixed(0)}%
                </span>
                <span
                  className={`w-14 text-[10px] text-right ${
                    trend !== undefined
                      ? trend > 0
                        ? 'text-ember'
                        : trend < 0
                          ? 'text-success'
                          : 'text-mist/60'
                      : 'text-mist/50'
                  }`}
                >
                  {formatTrend(trend)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
